import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';

interface Application {
  id: number;
  submission_number?: string;
  debtor_name?: string;
  requested_amount?: number;
  status: string;
}

interface CreditRecentListProps {
  applications: Application[];
}

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-blue-100 text-blue-700',
  analysis: 'bg-indigo-100 text-indigo-700',
  approval: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  disbursed: 'bg-emerald-100 text-emerald-700',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Diajukan',
  analysis: 'Analisis',
  approval: 'Approval',
  approved: 'Disetujui',
  rejected: 'Ditolak',
  disbursed: 'Dicairkan',
};

export default function CreditRecentList({ applications }: CreditRecentListProps) {
  const navigate = useNavigate();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="bg-white border rounded-lg">
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="text-lg font-semibold">Aplikasi Kredit Terbaru</h3>
        <button
          onClick={() => navigate('/credit/applications')}
          className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
        >
          Lihat Semua
        </button>
      </div>
      <div className="p-4">
        {applications.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Belum ada aplikasi kredit</p>
          </div>
        ) : (
          <div className="space-y-3">
            {applications.map(app => (
              <div
                key={app.id}
                onClick={() => navigate(`/credit/submission?id=${app.id}`)}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">{app.submission_number || 'Draft'}</p>
                    <p className="text-sm text-gray-500">{app.debtor_name || '-'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold">{formatCurrency(app.requested_amount || 0)}</p>
                  <span
                    className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                      statusColors[app.status] || 'bg-gray-100'
                    }`}
                  >
                    {statusLabels[app.status] || app.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
